import * as XLSX from "xlsx";
import { pdf } from "@react-pdf/renderer";

/**
 * Export data to CSV format
 */
export const exportToCSV = (data: any[], filename: string) => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Convert data to CSV format
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      headers.map((header) => {
        const cell = row[header];
        // Escape cells that contain commas or quotes
        if (typeof cell === "string" && (cell.includes(",") || cell.includes('"'))) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell ?? "";
      }).join(",")
    ),
  ].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export data to Excel format using xlsx library
 */
export const exportToExcel = (data: any[], filename: string, sheetName = "Sheet1") => {
  if (!data || data.length === 0) {
    console.warn("No data to export");
    return;
  }

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Export multiple sheets to Excel
 */
export const exportMultipleSheetsToExcel = (
  sheets: Array<{ data: any[]; sheetName: string }>,
  filename: string
) => {
  if (!sheets || sheets.length === 0) {
    console.warn("No data to export");
    return;
  }

  const workbook = XLSX.utils.book_new();

  sheets.forEach(({ data, sheetName }) => {
    if (data && data.length > 0) {
      const worksheet = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }
  });

  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Format data for export by flattening nested objects
 */
export const flattenDataForExport = (data: any[]): any[] => {
  return data.map((item) => {
    const flattened: any = {};

    Object.keys(item).forEach((key) => {
      if (typeof item[key] === "object" && item[key] !== null && !Array.isArray(item[key])) {
        // Flatten nested object
        Object.keys(item[key]).forEach((nestedKey) => {
          flattened[`${key}_${nestedKey}`] = item[key][nestedKey];
        });
      } else if (Array.isArray(item[key])) {
        // Convert array to string
        flattened[key] = item[key].join(", ");
      } else {
        flattened[key] = item[key];
      }
    });

    return flattened;
  });
};

/**
 * Download file from URL (for backend-generated exports)
 */
export const downloadFileFromUrl = (url: string, filename?: string) => {
  const link = document.createElement("a");
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export PDF document to file
 * @param pdfDocument React PDF Document element created with @react-pdf/renderer
 * @param filename Output filename (without extension)
 */
export const exportToPDF = async (pdfDocument: React.ReactElement, filename: string) => {
  try {
    // Generate PDF blob from React element
    const blob = await pdf(pdfDocument).toBlob();

    // Create download link
    const link = window.document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.pdf`);
    link.style.visibility = "hidden";
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
};
